import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  listEmployeeAllowanceTypeRecords,
  syncEmployeeAllowanceTypesFromApi,
} from "@/lib/hr/employeeAllowanceTypesStore";
import {
  listEmployeeDeductionTypeRecords,
  syncEmployeeDeductionTypesFromApi,
} from "@/lib/hr/employeeDeductionTypesStore";
import { getEmployeeById, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  createSalaryIncrementBatch,
  salaryIncrementApiErrorMessage,
  type SalaryIncrementType,
} from "@/lib/hr/salaryIncrementsStore";
import { format } from "date-fns";
import { Banknote, Coins, Loader2, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type BatchRow = {
  apply: boolean;
  incrementType: SalaryIncrementType;
  incrementValue: string;
};

function commaAmount(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const raw = String(v).trim();
  if (!raw) return "—";
  const n = Number(raw.replace(/,/g, ""));
  if (!Number.isFinite(n)) return raw;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

function parseAmountNumber(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const raw = String(v).trim();
  if (!raw) return null;
  const n = Number(raw.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function previewIncrementedAmount(params: {
  current: string | number | null | undefined;
  incrementType: SalaryIncrementType;
  incrementValue: string;
}): string | null {
  const cur = parseAmountNumber(params.current);
  const inc = parseAmountNumber(params.incrementValue);
  if (cur == null || inc == null) return null;
  const next = params.incrementType === "percentage" ? cur + (cur * inc) / 100 : cur + inc;
  return commaAmount(next);
}

function buildAllowanceMap(emp: EmployeeRecord | undefined): Record<string, BatchRow> {
  const m: Record<string, BatchRow> = {};
  for (const a of emp?.allowances ?? []) {
    m[a.allowanceTypeId] = { apply: false, incrementType: "percentage", incrementValue: "" };
  }
  return m;
}

function buildDeductionMap(emp: EmployeeRecord | undefined): Record<string, BatchRow> {
  const m: Record<string, BatchRow> = {};
  for (const d of emp?.deductions ?? []) {
    m[d.deductionTypeId] = { apply: false, incrementType: "percentage", incrementValue: "" };
  }
  return m;
}

function allowanceDisplayName(
  emp: EmployeeRecord | undefined,
  allowanceTypes: { id: string; label: string }[],
  allowanceTypeId: string,
): string {
  const fromEmp = emp?.allowances?.find((x) => x.allowanceTypeId === allowanceTypeId)?.name;
  if (fromEmp) return fromEmp;
  return allowanceTypes.find((x) => x.id === allowanceTypeId)?.label ?? "Allowance";
}

function deductionDisplayName(
  emp: EmployeeRecord | undefined,
  deductionTypes: { id: string; label: string }[],
  deductionTypeId: string,
): string {
  const fromEmp = emp?.deductions?.find((x) => x.deductionTypeId === deductionTypeId)?.name;
  if (fromEmp) return fromEmp;
  return deductionTypes.find((x) => x.id === deductionTypeId)?.label ?? "Deduction";
}

export type SalaryIncrementCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  onSaved?: () => void;
};

export function SalaryIncrementCreateDialog({
  open,
  onOpenChange,
  employeeId,
  onSaved,
}: SalaryIncrementCreateDialogProps) {
  const [employee, setEmployee] = useState<EmployeeRecord | undefined>(() => getEmployeeById(employeeId));
  const [allowanceTypesTick, setAllowanceTypesTick] = useState(0);
  const [deductionTypesTick, setDeductionTypesTick] = useState(0);
  const allowanceTypes = useMemo(() => listEmployeeAllowanceTypeRecords(), [allowanceTypesTick]);
  const deductionTypes = useMemo(() => listEmployeeDeductionTypeRecords(), [deductionTypesTick]);

  const [effectiveFrom, setEffectiveFrom] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [reason, setReason] = useState("");
  const [applyBasic, setApplyBasic] = useState(false);
  const [basicBatchType, setBasicBatchType] = useState<SalaryIncrementType>("percentage");
  const [basicBatchValue, setBasicBatchValue] = useState("");
  const [allowanceMap, setAllowanceMap] = useState<Record<string, BatchRow>>({});
  const [deductionMap, setDeductionMap] = useState<Record<string, BatchRow>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const emp = getEmployeeById(employeeId);
    setEmployee(emp);
    setEffectiveFrom(format(new Date(), "yyyy-MM-dd"));
    setReason("");
    setApplyBasic(false);
    setBasicBatchType("percentage");
    setBasicBatchValue("");
    setAllowanceMap(buildAllowanceMap(emp));
    setDeductionMap(buildDeductionMap(emp));
    void syncEmployeeAllowanceTypesFromApi()
      .catch(() => {})
      .finally(() => setAllowanceTypesTick((t) => t + 1));
    void syncEmployeeDeductionTypesFromApi()
      .catch(() => {})
      .finally(() => setDeductionTypesTick((t) => t + 1));
  }, [open, employeeId]);

  const close = () => onOpenChange(false);

  const save = async () => {
    if (saving || !employeeId) return;
    if (!effectiveFrom.trim()) return toast.error("Effective date is required.");

    const allowancePayload: Array<{ allowanceId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
    for (const [aid, row] of Object.entries(allowanceMap)) {
      if (!row.apply) continue;
      if (!row.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
        return toast.error(`Enter a valid increment value for ${allowanceDisplayName(employee, allowanceTypes, aid)}.`);
      }
      allowancePayload.push({
        allowanceId: aid,
        incrementType: row.incrementType,
        incrementValue: row.incrementValue,
      });
    }

    const deductionPayload: Array<{ deductionId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
    for (const [did, row] of Object.entries(deductionMap)) {
      if (!row.apply) continue;
      if (!row.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
        return toast.error(`Enter a valid increment value for ${deductionDisplayName(employee, deductionTypes, did)}.`);
      }
      deductionPayload.push({
        deductionId: did,
        incrementType: row.incrementType,
        incrementValue: row.incrementValue,
      });
    }

    let basicPayload: { incrementType: SalaryIncrementType; incrementValue: string } | null = null;
    if (applyBasic) {
      if (!basicBatchValue.trim() || !Number.isFinite(Number(basicBatchValue))) {
        return toast.error("Enter a valid basic salary increment value.");
      }
      basicPayload = { incrementType: basicBatchType, incrementValue: basicBatchValue };
    }

    if (!basicPayload && allowancePayload.length === 0 && deductionPayload.length === 0) {
      return toast.error("Select basic salary and/or at least one allowance or deduction to increment.");
    }

    setSaving(true);
    try {
      const result = await createSalaryIncrementBatch({
        employeeId,
        effectiveFrom,
        reason: reason.trim() ? reason.trim() : null,
        basic: basicPayload,
        allowances: allowancePayload,
        deductions: deductionPayload,
      });
      toast.success(result.message);
      close();
      onSaved?.();
    } catch (e) {
      toast.error(salaryIncrementApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const basicPreview = applyBasic
    ? previewIncrementedAmount({
        current: employee?.basicSalary,
        incrementType: basicBatchType,
        incrementValue: basicBatchValue,
      })
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto w-[min(96vw,820px)] sm:max-w-[820px]">
        <DialogHeader>
          <DialogTitle>New salary & allowance increment</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 py-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Effective from (all lines in this run) <span className="text-destructive">*</span>
              </Label>
              <DatePartsPicker
                value={effectiveFrom}
                onChange={setEffectiveFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 5}
                placeholder="Select effective date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Reason (optional)</Label>
              <Input
                className="glass-input h-10"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Annual increment"
              />
            </div>
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/10 p-3 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="emp-inc-basic"
                checked={applyBasic}
                onCheckedChange={(v) => setApplyBasic(v === true)}
                className="mt-1"
              />
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="emp-inc-basic" className="text-sm font-semibold text-foreground cursor-pointer flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-emerald-600 shrink-0" />
                    Basic salary
                  </Label>
                  <div className="text-xs text-muted-foreground shrink-0 font-mono">
                    <span className="text-foreground">{commaAmount(employee?.basicSalary)}</span>
                    {basicPreview ? (
                      <>
                        <span className="text-muted-foreground"> → </span>
                        <span className="font-semibold text-emerald-600">{basicPreview}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-1 min-w-0">
                  <Select
                    value={basicBatchType}
                    onValueChange={(v) => setBasicBatchType(v as SalaryIncrementType)}
                    disabled={!applyBasic}
                  >
                    <SelectTrigger className="glass-input h-10 w-[180px] rounded-r-none border-r-0">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed amount</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1 min-w-0">
                    <Input
                      className="glass-input h-10 w-full rounded-l-none pr-12"
                      value={basicBatchValue}
                      onChange={(e) => setBasicBatchValue(e.target.value)}
                      placeholder={basicBatchType === "percentage" ? "Value (e.g. 10)" : "Value (e.g. 5000)"}
                      inputMode="decimal"
                      disabled={!applyBasic}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      {basicBatchType === "percentage" ? "%" : "Amt"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border/60 bg-muted/10 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
                  <Coins className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="truncate">Allowances</span>
                </div>
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (!v) return;
                    setAllowanceMap((m) =>
                      m[v] ? m : { ...m, [v]: { apply: false, incrementType: "percentage", incrementValue: "" } },
                    );
                  }}
                >
                  <SelectTrigger className="glass-input h-9 w-[200px]">
                    <SelectValue placeholder="Add allowance…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowanceTypes
                      .filter((t) => !(t.id in allowanceMap))
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {Object.keys(allowanceMap).length === 0 ? (
                <p className="text-sm text-muted-foreground">No allowances in this increment. Use the dropdown to add one.</p>
              ) : (
                <div className="space-y-3 max-h-[min(40vh,320px)] overflow-y-auto pr-1">
                  {Object.keys(allowanceMap)
                    .sort((a, b) =>
                      allowanceDisplayName(employee, allowanceTypes, a).localeCompare(
                        allowanceDisplayName(employee, allowanceTypes, b),
                      ),
                    )
                    .map((allowanceTypeId) => {
                      const row = allowanceMap[allowanceTypeId]!;
                      const empAllowance = employee?.allowances?.find((x) => x.allowanceTypeId === allowanceTypeId);
                      const base = empAllowance?.amount ?? 0;
                      const name = allowanceDisplayName(employee, allowanceTypes, allowanceTypeId);
                      const next = row.apply
                        ? previewIncrementedAmount({
                            current: base,
                            incrementType: row.incrementType,
                            incrementValue: row.incrementValue,
                          })
                        : null;
                      return (
                        <div key={allowanceTypeId} className="rounded-lg border border-border/50 bg-background/60 p-2.5 space-y-2">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`emp-allow-${allowanceTypeId}`}
                              checked={row.apply}
                              onCheckedChange={(v) =>
                                setAllowanceMap((m) => ({
                                  ...m,
                                  [allowanceTypeId]: { ...row, apply: v === true },
                                }))
                              }
                              className="mt-1"
                            />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="flex items-center justify-between gap-3">
                                <Label htmlFor={`emp-allow-${allowanceTypeId}`} className="text-sm font-semibold truncate">
                                  {name}
                                </Label>
                                <div className="text-xs text-muted-foreground shrink-0 font-mono">
                                  <span className="text-foreground">{commaAmount(base)}</span>
                                  {next ? (
                                    <>
                                      <span className="text-muted-foreground"> → </span>
                                      <span className="font-semibold text-emerald-600">{next}</span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              <div className="flex flex-1 min-w-0">
                                <Select
                                  value={row.incrementType}
                                  onValueChange={(v) =>
                                    setAllowanceMap((m) => ({
                                      ...m,
                                      [allowanceTypeId]: { ...row, incrementType: v as SalaryIncrementType },
                                    }))
                                  }
                                  disabled={!row.apply}
                                >
                                  <SelectTrigger className="glass-input h-10 w-[180px] rounded-r-none border-r-0">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percentage">Percentage</SelectItem>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="relative flex-1 min-w-0">
                                  <Input
                                    className="glass-input h-10 w-full rounded-l-none pr-12"
                                    value={row.incrementValue}
                                    onChange={(e) =>
                                      setAllowanceMap((m) => ({
                                        ...m,
                                        [allowanceTypeId]: { ...row, incrementValue: e.target.value },
                                      }))
                                    }
                                    placeholder={row.incrementType === "percentage" ? "Value (e.g. 10)" : "Value (e.g. 500)"}
                                    inputMode="decimal"
                                    disabled={!row.apply}
                                  />
                                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                    {row.incrementType === "percentage" ? "%" : "Amt"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border/60 bg-muted/10 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
                  <Coins className="w-4 h-4 text-sky-600 shrink-0" />
                  <span className="truncate">Deductions</span>
                </div>
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (!v) return;
                    setDeductionMap((m) =>
                      m[v] ? m : { ...m, [v]: { apply: false, incrementType: "percentage", incrementValue: "" } },
                    );
                  }}
                >
                  <SelectTrigger className="glass-input h-9 w-[200px]">
                    <SelectValue placeholder="Add deduction…" />
                  </SelectTrigger>
                  <SelectContent>
                    {deductionTypes
                      .filter((t) => !(t.id in deductionMap))
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {Object.keys(deductionMap).length === 0 ? (
                <p className="text-sm text-muted-foreground">No deductions in this increment. Use the dropdown to add one.</p>
              ) : (
                <div className="space-y-3 max-h-[min(40vh,320px)] overflow-y-auto pr-1">
                  {Object.keys(deductionMap)
                    .sort((a, b) =>
                      deductionDisplayName(employee, deductionTypes, a).localeCompare(
                        deductionDisplayName(employee, deductionTypes, b),
                      ),
                    )
                    .map((deductionTypeId) => {
                      const row = deductionMap[deductionTypeId]!;
                      const empDeduction = employee?.deductions?.find((x) => x.deductionTypeId === deductionTypeId);
                      const base = empDeduction?.amount ?? 0;
                      const name = deductionDisplayName(employee, deductionTypes, deductionTypeId);
                      const next = row.apply
                        ? previewIncrementedAmount({
                            current: base,
                            incrementType: row.incrementType,
                            incrementValue: row.incrementValue,
                          })
                        : null;
                      return (
                        <div key={deductionTypeId} className="rounded-lg border border-border/50 bg-background/60 p-2.5 space-y-2">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`emp-ded-${deductionTypeId}`}
                              checked={row.apply}
                              onCheckedChange={(v) =>
                                setDeductionMap((m) => ({
                                  ...m,
                                  [deductionTypeId]: { ...row, apply: v === true },
                                }))
                              }
                              className="mt-1"
                            />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="flex items-center justify-between gap-3">
                                <Label htmlFor={`emp-ded-${deductionTypeId}`} className="text-sm font-semibold truncate">
                                  {name}
                                </Label>
                                <div className="text-xs text-muted-foreground shrink-0 font-mono">
                                  <span className="text-foreground">{commaAmount(base)}</span>
                                  {next ? (
                                    <>
                                      <span className="text-muted-foreground"> → </span>
                                      <span className="font-semibold text-emerald-600">{next}</span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              <div className="flex flex-1 min-w-0">
                                <Select
                                  value={row.incrementType}
                                  onValueChange={(v) =>
                                    setDeductionMap((m) => ({
                                      ...m,
                                      [deductionTypeId]: { ...row, incrementType: v as SalaryIncrementType },
                                    }))
                                  }
                                  disabled={!row.apply}
                                >
                                  <SelectTrigger className="glass-input h-10 w-[180px] rounded-r-none border-r-0">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percentage">Percentage</SelectItem>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="relative flex-1 min-w-0">
                                  <Input
                                    className="glass-input h-10 w-full rounded-l-none pr-12"
                                    value={row.incrementValue}
                                    onChange={(e) =>
                                      setDeductionMap((m) => ({
                                        ...m,
                                        [deductionTypeId]: { ...row, incrementValue: e.target.value },
                                      }))
                                    }
                                    placeholder={row.incrementType === "percentage" ? "Value (e.g. 10)" : "Value (e.g. 500)"}
                                    inputMode="decimal"
                                    disabled={!row.apply}
                                  />
                                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                    {row.incrementType === "percentage" ? "%" : "Amt"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="glass" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="premium" className="gap-2" onClick={() => void save()} disabled={saving || !employeeId}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
